'use client';
import React, { useEffect } from 'react';
import { useTranslations } from 'next-intl';

import { useRestaurantStore } from '@/stores/restaurants';
import useStore from '@/stores/useStore';
import STOREFRONT from '@/lib/utils/translation';
import SearchCard from '@/components/search/SearchCard';
import { search } from '@/clients/customer/restaurants';
import Button from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { cn } from '@/lib/utils';

interface Props {
  data: any;
  pageType: 'delivery' | 'pickup' | 'all';
  queryParams?: { [key: string]: any };
  params?: { [key: string]: string };
  showLoadMore?: boolean;
}

export default function RestaurantList({
  data,
  pageType,
  queryParams,
  params,
  showLoadMore,
}: Props) {
  const restaurantsData = useStore(
    useRestaurantStore,
    (state) => state.restaurantsData
  );

  const isLoading = useStore(useRestaurantStore, (state) => state.isLoading);

  const { setData, setIsMoreLoading, isMoreLoading } = useRestaurantStore();

  const t = useTranslations();

  useEffect(() => {
    if (data) {
      setData(data);
    }
  }, [data, setData]);

  const fetchMoreData = async () => {
    setIsMoreLoading(true);
    try {
      const response = await search(
        {
          ...queryParams,
          ...{
            page: restaurantsData ? restaurantsData?.meta?.current_page + 1 : 1,
          },
        },
        {
          ['Accept-Language']: params ? params.locale : '',
        }
      );
      const newRestaurants = response.data;
      const updatedResData = {
        ...restaurantsData,
        meta: newRestaurants.meta,
        data: restaurantsData
          ? [...restaurantsData.data, ...newRestaurants.data]
          : [],
      };

      setData(updatedResData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setIsMoreLoading(false);
  };

  if (isLoading) {
    return (
      <div className="mb-[34px] mt-[23px] grid grid-cols-1 gap-[32px] sm:grid-cols-2 lg:mb-[60px] lg:mt-[28px] lg:grid-cols-3">
        {Array.from({ length: 8 }, (_, i) => (
          <div
            key={i}
            className="h-[300px] w-full animate-pulse rounded-[10px] bg-slate-400"
          ></div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="mb-[34px] mt-[23px] grid grid-cols-1 gap-[32px] sm:grid-cols-2 lg:mb-[60px] lg:mt-[28px] lg:grid-cols-3">
        {restaurantsData?.data &&
          restaurantsData.data?.map((deliveryData: any) => {
            return (
              <SearchCard
                type={pageType}
                restaurant={deliveryData}
                key={deliveryData.id}
              />
            );
          })}
      </div>

      {restaurantsData &&
        showLoadMore &&
        restaurantsData?.data?.length > 0 &&
        restaurantsData?.meta.last_page >
          restaurantsData?.meta.current_page && (
          <div className="mb-20 mt-4 flex flex-row justify-center text-center">
            <Button
              rounded
              className={cn('w-[200px] py-4')}
              type="button"
              onClick={fetchMoreData}
              disabled={isMoreLoading}
            >
              {isMoreLoading ? (
                <LoadingSpinner />
              ) : (
                t(STOREFRONT.SEARCH + '.show_more')
              )}
            </Button>
          </div>
        )}

      {restaurantsData?.data?.length === 0 && (
        <div className="mx-auto my-[34px] grid w-full place-content-center gap-y-[30px] text-center md:w-[80%] xl:w-[60%]">
          <span className="whitespace-pre-wrap text-h3 font-medium">
            {t(STOREFRONT.SEARCH + '.no_restaurant')}
          </span>
          {/* <span className="whitespace-pre-wrap text-lg font-black text-primary">
            {t(STOREFRONT.SEARCH + '.no_restaurant_subheader')}
          </span> */}
        </div>
      )}
    </>
  );
}
